import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Flex,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Table,
  Typography,
  message,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  CameraOutlined,
} from '@ant-design/icons';
import PropTypes from 'prop-types';
import { getAuth } from 'firebase/auth';
import {
  notificationTimeout,
  notificationType,
  scrollHeight,
} from '../common/constants';
import notValid from '../common/notValid';
import pluralise from '../common/pluralise';
import {
  firListPlateCamerasForSite,
  firAddPlateCamera,
  firUpdatePlateCamera,
  firDeletePlateCamera,
  firGetDoorChannels,
} from '../firebase';

const { Text } = Typography;

/**
 * Plate Cameras — per-site mapping editor for license-plate scanner
 * camera-to-door bindings (rev 123.3).
 *
 * Top-level collection `plateCameras/{cam_id}` carrying
 * { siteId, doorId, label, addedBy, addedAt, updatedAt }.  Doc id IS
 * the camera's cam_id (MAC or vendor UID) so the carplatescanneragent
 * server can do a single getDoc lookup keyed by what the camera self-
 * reports (no extra mapping table needed).
 *
 * Tab gating: superAdmin + root (mounted in siteConfig.jsx).
 */
export default function SiteConfigPlateCameras({ siteId }) {
  const [cameras, setCameras] = useState([]);
  const [doors, setDoors] = useState({});
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingCamera, setEditingCamera] = useState(null);

  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();

  const { timeoutError, timeoutSuccess } = notificationTimeout;
  const { typeError, typeSuccess } = notificationType;

  const openMessage = useCallback(
    (content, duration, type) => {
      messageApi.open({ content, duration, type });
    },
    [messageApi],
  );

  const fetchAll = useCallback(
    async (id) => {
      if (notValid(id)) return;
      setLoading(true);
      try {
        const [list, doorChannels] = await Promise.all([
          firListPlateCamerasForSite(id),
          firGetDoorChannels(id),
        ]);
        setCameras(list);
        setDoors(doorChannels || {});
      } catch (error) {
        console.error('-- Error fetching plate cameras:', error);
        openMessage('Error fetching plate cameras', timeoutError, typeError);
      } finally {
        setLoading(false);
      }
    },
    [openMessage, timeoutError, typeError],
  );

  useEffect(() => {
    fetchAll(siteId);
  }, [siteId, fetchAll]);

  const doorOptions = useMemo(() => Object.entries(doors).map(([code, meta]) => ({
    value: code,
    label: `Door ${code}${meta?.label ? ` — ${meta.label}` : ''}`,
  })), [doors]);

  const handleAdd = () => {
    setEditingCamera(null);
    form.resetFields();
    setModalOpen(true);
  };

  const handleEdit = (record) => {
    setEditingCamera(record);
    form.setFieldsValue({
      camId: record.id,
      doorId: record.doorId,
      label: record.label,
    });
    setModalOpen(true);
  };

  const handleDelete = async (camId) => {
    try {
      await firDeletePlateCamera(camId);
      openMessage('Camera removed', timeoutSuccess, typeSuccess);
      fetchAll(siteId);
    } catch (error) {
      console.error('-- Error deleting camera:', error);
      openMessage('Error removing camera', timeoutError, typeError);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const camId = String(values.camId).trim();
      const doorId = String(values.doorId).trim();
      const label = String(values.label).trim();
      const uid = getAuth().currentUser?.uid || '';

      if (editingCamera) {
        await firUpdatePlateCamera(editingCamera.id, { doorId, label });
        openMessage('Camera updated', timeoutSuccess, typeSuccess);
      } else {
        await firAddPlateCamera(camId, { siteId, doorId, label }, uid);
        openMessage('Camera added', timeoutSuccess, typeSuccess);
      }
      setModalOpen(false);
      fetchAll(siteId);
    } catch (error) {
      if (error?.errorFields) return;
      console.error('-- Error saving camera:', error);
      openMessage('Error saving camera', timeoutError, typeError);
    }
  };

  const columns = [
    {
      title: 'Camera ID',
      dataIndex: 'id',
      key: 'id',
      width: 220,
      render: (v) => <code style={{ fontSize: 12 }}>{v}</code>,
      sorter: (a, b) => a.id.localeCompare(b.id),
    },
    {
      title: 'Door',
      dataIndex: 'doorId',
      key: 'doorId',
      width: 200,
      render: (v) => {
        if (!v) return <Text type="secondary"><em>unassigned</em></Text>;
        const label = doors[v]?.label;
        return (
          <Space>
            <code>{v}</code>
            {label ? <Text type="secondary">{label}</Text> : null}
          </Space>
        );
      },
    },
    {
      title: 'Label',
      dataIndex: 'label',
      key: 'label',
      render: (v) => v || <Text type="secondary"><em>(none)</em></Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => handleEdit(record)}>Edit</Button>
          <Popconfirm
            title="Remove this camera mapping?"
            description="The agent will not be able to attribute this camera's recognitions to a door until re-added."
            onConfirm={() => handleDelete(record.id)}
            okText="Remove"
            okButtonProps={{ danger: true }}
            cancelText="Cancel"
          >
            <Button icon={<DeleteOutlined />} danger>Remove</Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      {contextHolder}
      <Flex justify="space-between" align="center" style={{ marginBottom: 16 }}>
        <span>
          <CameraOutlined style={{ marginRight: 8 }} />
          {cameras.length} {pluralise(cameras.length, 'camera')} mapped to doors at this site.
          {' '}The agent reads this on every recognition to attribute the event to the correct door.
        </span>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
          Add Camera
        </Button>
      </Flex>

      <Table
        columns={columns}
        dataSource={cameras}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 50 }}
        scroll={{ y: scrollHeight }}
      />

      <Modal
        title={editingCamera ? 'Edit Camera' : 'Add Camera'}
        open={modalOpen}
        onOk={handleSubmit}
        onCancel={() => setModalOpen(false)}
        okText="Save"
        destroyOnHidden
        width={560}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            label="Camera ID (cam_id)"
            name="camId"
            extra={(
              <>
                The camera&apos;s MAC address or vendor UID — exactly the value
                the camera sends in the <code>cam_id</code> field of its HTTP
                POST mode 5 payloads. Find it in the camera&apos;s admin web UI
                or the agent&apos;s journalctl log on first connect.
              </>
            )}
            rules={[
              { required: true, message: 'cam_id is required' },
              { min: 4, max: 64, message: 'cam_id should be 4–64 characters' },
            ]}
          >
            <Input
              disabled={!!editingCamera}
              placeholder="e.g. 0001aa00000d"
              autoComplete="off"
            />
          </Form.Item>
          <Form.Item
            label="Door"
            name="doorId"
            extra="Which door at this site does the camera face? Sourced from the Door Channels tab — add the door there first if you don't see it here."
            rules={[{ required: true, message: 'Door is required' }]}
          >
            <Select
              options={doorOptions}
              placeholder={doorOptions.length === 0 ? 'No doors configured for this site yet' : 'Choose a door'}
              disabled={doorOptions.length === 0}
            />
          </Form.Item>
          <Form.Item
            label="Label"
            name="label"
            extra="Human description shown in MA web only (not sent to the camera). Examples: 'Front gate entry cam', 'Side exit cam'."
            rules={[
              { required: true, message: 'Label is required' },
              { min: 1, max: 128, message: 'Label should be 1–128 characters' },
            ]}
          >
            <Input placeholder="e.g. Front gate entry cam" autoComplete="off" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}

SiteConfigPlateCameras.propTypes = {
  siteId: PropTypes.string.isRequired,
};
